import type {
	CoordinationSettingsData,
	ControllerSettingsData,
	PhaseMappingData,
	PhaseSettingsData,
	SequenceData,
	SignalData,
	SignalErrors,
	TimeOfDayData,
} from '@app/models/optimus-configuration'
import { SIGNAL_PROGRESS_STATE_ENUM } from '@app/models/optimus-configuration'
import m from 'mithril'
import dayjs from 'dayjs'
import dayjs_utc from 'dayjs/plugin/utc'
import dayjs_tz from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
dayjs.extend(dayjs_utc)
dayjs.extend(dayjs_tz)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

import s from '../styles.module.scss'

import OptimusConfigurationModel from '@app/models/optimus-configuration'
import { generateTodDescription, isSameTodDescription } from '@app/helpers/att-args'
import { validateTimes } from '../time-of-day-schedule/helpers/time-validation'
import { validateInput } from '../coordination-settings/helpers/timingValidation'
import { validateTimingSettings } from '../phase-settings/helpers/validate-timing-settings'
import { validatePhaseMappings } from '../phase-mapping/helpers/validate-phase-mappings'

import { isDateRangeCoversDateRange } from '@shared/utils/datetime'
import { validateSequence } from '../sequences/helpers/sequence-validations'
import type { SignalConfigurationTODSlice } from '#frontend/queries/optimus/optimus-report-request.ts'

interface Signal {
	id: string
	name: string
	name_short?: string
	signalStatus?: string
	[key: string]: any
}

export const getSignalOptionsWithCorrectLabel = (signals: Signal[]) => {
	return signals.map((signal) => {
		const baseLabel = signal.name_short || signal.name || `Signal ${signal.id}`
		const status = signal.signalStatus

		let dotClass = ''
		let dotContent: m.Children = null

		switch (status) {
			case SIGNAL_PROGRESS_STATE_ENUM['not-started']:
				dotClass = s.dot_not_started
				break

			case SIGNAL_PROGRESS_STATE_ENUM['in-progress-no-errors']:
				dotClass = s.dot_in_progress
				break

			case SIGNAL_PROGRESS_STATE_ENUM['in-progress-errors']:
				dotClass = s.error_count
				dotContent = m(
					'div',
					{ class: s.error_cross },
					m('svg', { width: 16, height: 16, viewBox: '0 0 16 16', xmlns: 'http://www.w3.org/2000/svg' }, [
						m('circle', { cx: 8, cy: 8, r: 6, fill: 'white', 'fill-opacity': 0.1 }),
						m('path', {
							d: 'M5 8H11M8 5V11',
							stroke: 'white',
							'stroke-linecap': 'round',
							'stroke-linejoin': 'round',
						}),
					]),
				)
				break

			case SIGNAL_PROGRESS_STATE_ENUM['complete']:
				dotClass = s.dot_complete
				dotContent = m(
					'div',
					{ class: s.completed_checkmark },
					m('svg', { width: 8, height: 6, viewBox: '0 0 8 6', xmlns: 'http://www.w3.org/2000/svg' }, [
						m('path', { d: 'M1 2.5L3 4.5L7 1', stroke: 'white' }),
					]),
				)
				break

			default:
				dotClass = s.dot_not_started
		}

		const LabelComp = () =>
			m('div', { class: s.signal_option_wrapper }, [
				m('div', { class: s.base_label, key: `status-${status}-${signal.id}-label` }, baseLabel),
				m('div', { class: [s.status_dot, dotClass].join(' '), key: `status-${status}-${signal.id}` }, dotContent),
			])

		return {
			value: String(signal.id),
			label: LabelComp,
		}
	})
}

interface IGetTodConfigurationStatusPerSignalArgs {
	phaseSettings: PhaseSettingsData[]
	coordinationSettings: CoordinationSettingsData[]
	timeOfDaySchedule: TimeOfDayData[]
	phaseMapping: PhaseMappingData[]
	controllerSettings: ControllerSettingsData[]
	sequences: SequenceData[]
	allTodSchedules: Record<string, TimeOfDayData[]>
	totalSignals: number
}
function getTodConfigurationStatusPerSignal({
	phaseSettings,
	coordinationSettings,
	timeOfDaySchedule,
	phaseMapping,
	controllerSettings,
	sequences,
	allTodSchedules,
	totalSignals,
}: IGetTodConfigurationStatusPerSignalArgs) {
	const summaries: Record<
		string,
		{
			usedBy: number
			totalSignals: number
			fullyShared: boolean
			notUsedBySignals: string[]
			notFullyConfigured: boolean
			missingLinkedSettings: string[]
		}
	> = {}
	const allSignalIds = Object.keys(allTodSchedules)

	timeOfDaySchedule.forEach((referenceTod) => {
		const matchingSignalIds: string[] = []
		const missingLinkedSettings: string[] = []
		const signalIdToMatchingTod: Record<string, TimeOfDayData> = {}

		for (const sigId of allSignalIds) {
			const tods = allTodSchedules[sigId] || []
			const match = tods.find((t) => isSameTodDescription(referenceTod, t))
			if (match) {
				matchingSignalIds.push(sigId)
				signalIdToMatchingTod[sigId] = match
			}
		}

		const notUsedBySignals = allSignalIds.filter((id) => !matchingSignalIds.includes(id))

		matchingSignalIds.forEach((sigId) => {
			const tod = signalIdToMatchingTod[sigId]
			if (referenceTod.time_of_day_id !== tod.time_of_day_id) {
				return
			}
			const missing: string[] = []

			const foundPhaseSetting = phaseSettings.find((ps) => ps.phase_settings_id === tod.phase_settings_id)
			const foundPhaseMapping = phaseMapping.find((pm) => pm.phase_settings_id === foundPhaseSetting?.phase_settings_id)
			const foundCoordinationSettings = coordinationSettings.find(
				(cs) => cs.coordination_settings_id === tod.coordination_settings_id,
			)
			// Check if controller settings exist for this signal (not tied to specific TOD)
			// Controller settings must be saved (not new-/default- rows) AND have valid data
			const hasValidControllerSettings = controllerSettings.some((setting) => {
				// Skip new/default rows that haven't been saved
				if (
					setting.controller_settings_id.startsWith('new-') ||
					setting.controller_settings_id.startsWith('default-')
				) {
					return false
				}
				// Check if the setting has valid data
				const data = setting.controller_settings_data || {}
				return !!data.forceOffMode && !!data.offsetReferencePoint
			})

			const hasValidSequence = sequences.some((sequence) => {
				// Skip new/default rows that haven't been saved
				if (sequence?.sequence_id?.startsWith('new-') || sequence?.sequence_id?.startsWith('default-')) {
					return false
				}
				// Check if the sequence has valid data
				// const data = sequence.controller_settings_data || {};
				return true
			})

			if (!foundPhaseSetting) {
				missing.push('phase-setting')
			}

			if (!foundCoordinationSettings) {
				missing.push('coordination-setting')
			}
			if (!foundPhaseMapping) {
				missing.push('phase-mapping')
			}
			if (!hasValidControllerSettings) {
				missing.push('controller-setting')
			}
			if (!hasValidSequence) {
				missing.push('sequence')
			}

			if (missing.length > 0) {
				missingLinkedSettings.push(...missing)
			}
		})

		const todId = referenceTod.time_of_day_id

		summaries[todId] = {
			usedBy: matchingSignalIds.length,
			totalSignals,
			fullyShared: matchingSignalIds.length === totalSignals,
			notUsedBySignals,
			notFullyConfigured: missingLinkedSettings.length > 0,
			missingLinkedSettings,
		}
	})

	const todsInAllSignals = timeOfDaySchedule.filter((tod) => {
		const todDetails = summaries[tod.time_of_day_id]
		return todDetails.fullyShared
	})

	const errors: Record<string, string> = {}
	if (!todsInAllSignals.length) {
		const fullyUsedTodErrorKey = `tod-configuration-fully-used`
		errors[fullyUsedTodErrorKey] = 'A TOD must be used across all signals'
	} else {
		todsInAllSignals.forEach((tod) => {
			const todDetails = summaries[tod.time_of_day_id]
			if (todDetails.missingLinkedSettings.length) {
				const linkErrorKey = `${tod.time_of_day_id}-missing-linked-plans`
				const formattedMissingLinks = todDetails.missingLinkedSettings.join(', ').replaceAll('-', ' ')
				errors[linkErrorKey] = `TOD ${tod.name} - Missing links to ${formattedMissingLinks}`
			}
		})
	}

	// return errors;
	return { completedConfigurationsErrors: errors, completedConfigurationDetails: summaries }
}

interface IGetSignalProgressStatusArgs {
	phaseSettings: PhaseSettingsData[]
	coordinationSettings: CoordinationSettingsData[]
	timeOfDaySchedule: TimeOfDayData[]
	phaseMapping: PhaseMappingData[]
	controllerSettings: ControllerSettingsData[]
	sequences: SequenceData[]
	tms_errors: SignalErrors['tms-errors']
	signal: SignalData
}
export const getSignalProgressStatus = ({
	phaseSettings,
	coordinationSettings,
	timeOfDaySchedule,
	phaseMapping,
	controllerSettings,
	sequences,
	tms_errors,
	signal,
}: IGetSignalProgressStatusArgs) => {
	const signalId = signal.id
	if (!phaseSettings || !coordinationSettings || !timeOfDaySchedule || !phaseMapping) {
		return {}
	}

	const phaseSettingsErrorObj: Record<string, Record<string, string>> = {}
	phaseSettings.forEach((setting) => {
		const baseSequence = [1, 2, 3, 4, 5, 6, 7, 8]
		phaseSettingsErrorObj[setting.phase_settings_id] = {}
		const isMarkedForDeletion = OptimusConfigurationModel.isMarkedForDeletion(
			'phase_setting',
			setting.phase_settings_id,
			setting.unique_setting_key,
			setting.valid_from || null,
			setting.valid_to || null,
		)
		if (isMarkedForDeletion) {
			return
		}

		// Surface backend error from phase settings, if any
		if (setting.error) {
			phaseSettingsErrorObj[setting.phase_settings_id]['backend-error'] = setting.error
		}

		baseSequence.forEach((phase) => {
			validateTimingSettings({
				allPhaseSettings: setting.phase_settings_data,
				phaseNumber: phase,
				currentErrors: phaseSettingsErrorObj[setting.phase_settings_id],
				phaseSequence: [1, 2, 3, 4, 5, 6, 7, 8],
				allSettingsRows: phaseSettings,
				externalPhaseSettingId: setting.phase_settings_external_id,
				currentPhaseSettingsId: setting.phase_settings_id, // Add this for date overlap validation
			})
		})
	})

	const coordinationSettingsErrorObj: Record<string, string> = {}
	coordinationSettings.forEach((setting) => {
		if (setting.error) {
			coordinationSettingsErrorObj[`${setting.coordination_settings_id}-backend-error`] = setting.error
		}

		validateInput({
			phasePlans: setting.coordination_settings_data,
			currentErrors: coordinationSettingsErrorObj,
			coordinationSettingsExternalId: setting.coordination_settings_external_id,
			allCoordinationSettingsRows: coordinationSettings,
			coordinationSettingsId: setting.coordination_settings_id,
			validFrom: setting.valid_from || undefined,
			validTo: setting.valid_to || undefined,
		})
	})

	const sequencesErrorObj: Record<string, string> = {}
	sequences.forEach((seq) => {
		if (seq.error) {
			sequencesErrorObj[`${seq.sequence_id}-backend-error`] = seq.error
		}

		validateSequence({
			sequence: seq,
			currentErrors: sequencesErrorObj,
		})
	})

	// Get available external IDs for dangling reference validation
	const availableSequenceExternalIds = sequences.map((cs) => cs.sequence_external_id)
	const availablePhaseExternalIds = phaseSettings.map((ps) => ps.phase_settings_external_id)

	const timeOfDayErrors =
		validateTimes(
			timeOfDaySchedule,
			availableSequenceExternalIds,
			availablePhaseExternalIds,
			coordinationSettings,
			sequences,
		) || {}

	// Surface backend errors from TOD rows
	timeOfDaySchedule.forEach((tod) => {
		if (tod.error) {
			// Align with tod-schedule-row key pattern: `${time_of_day_id}-${inputKey}`
			timeOfDayErrors[`${tod.time_of_day_id}-backend-error`] = tod.error
		}
	})

	const signalSpecs = OptimusConfigurationModel.signals?.find((s) => s.id === signalId)
	const { errors: rawPhaseMappingErrors } = validatePhaseMappings(
		signalSpecs,
		phaseSettings,
		phaseMapping,
		timeOfDaySchedule,
	)

	const phaseMappingErrors: Record<string, string> = { ...rawPhaseMappingErrors }

	// Surface backend errors from phase mappings
	phaseMapping.forEach((mapping) => {
		if (mapping.error) {
			phaseMappingErrors[`${mapping.phase_settings_id}-backend-error`] = mapping.error
		}
	})

	// Validate controller settings
	const controllerSettingsErrors: Record<string, string> = {}
	controllerSettings.forEach((setting) => {
		const isMarkedForDeletion = OptimusConfigurationModel.isMarkedForDeletion(
			'controller_setting',
			setting.controller_settings_id,
			'',
			setting.valid_from || null,
			setting.valid_to || null,
		)
		if (isMarkedForDeletion) {
			return
		}

		// Surface backend error from controller settings, if any
		if (setting.error) {
			controllerSettingsErrors[`${setting.controller_settings_id}-backend-error`] = setting.error
		}

		// Check for missing values
		const data = setting.controller_settings_data || {}
		const hasMissingValues = !data.forceOffMode || !data.offsetReferencePoint

		if (hasMissingValues) {
			const errorKey = `${setting.controller_settings_id}-missing-fields`
			controllerSettingsErrors[errorKey] =
				`Controller Settings ${setting.controller_settings_external_id}: Please select both Force-Off Mode and Offset Reference Point.`
		}
	})

	if (!controllerSettings.length) {
		const errorKey = `controller-settings-missing-setting`
		controllerSettingsErrors[errorKey] =
			`Controller Settings: Please select both Force-Off Mode and Offset Reference Point.`
	}

	const sequencesErrors: Record<string, string> = {}
	sequences.forEach((seq) => {
		const isMarkedForDeletion = OptimusConfigurationModel.isMarkedForDeletion(
			'sequences',
			seq.sequence_id || '',
			'',
			seq.valid_from || null,
			seq.valid_to || null,
		)
		if (isMarkedForDeletion) {
			return
		}

		// Surface backend error from controller settings, if any
		if (seq.error) {
			sequencesErrors[`${seq.sequence_id}-backend-error`] = seq.error
		}
	})

	const { completedConfigurationsErrors, completedConfigurationDetails } = getTodConfigurationStatusPerSignal({
		phaseSettings,
		coordinationSettings,
		timeOfDaySchedule,
		phaseMapping,
		controllerSettings,
		sequences,
		allTodSchedules: OptimusConfigurationModel.tod_schedules,
		totalSignals: OptimusConfigurationModel.signals.length,
	})

	const hasPhaseSettings = phaseSettings.length
	const hasCoordinationSettings = coordinationSettings.length
	const hasTimeOfDaySchedule = timeOfDaySchedule.length
	const hasPhaseMapping = phaseMapping.length
	const hasControllerSettings = controllerSettings.length
	const hasSequences = sequences.length
	const hasSettingsArr = [
		hasPhaseSettings,
		hasCoordinationSettings,
		hasTimeOfDaySchedule,
		hasPhaseMapping,
		hasControllerSettings,
		hasSequences,
	]

	const isNotStarted = hasSettingsArr.every((setting) => !setting)
	const isComplete = hasSettingsArr.every((setting) => setting)

	const phaseSettingsErrorCount = Object.values(phaseSettingsErrorObj as Record<string, object>).filter(
		(errorObj) => Object.keys(errorObj).length > 0,
	).length

	const uniquePhaseMappingErrors = Object.fromEntries(
		Object.entries(phaseMappingErrors).reduce<[string, string][]>((entries, [error_key, error_msg]) => {
			const unique_error_msgs = new Set(entries.map((e) => e[1]))
			return unique_error_msgs.has(error_msg) ? entries : [...entries, [error_key, error_msg]]
		}, []),
	)

	const errorCounts = {
		'phase-settings': phaseSettingsErrorCount,
		'coordination-settings': Object.keys(coordinationSettingsErrorObj).length,
		'tod-schedule': Object.keys(timeOfDayErrors).length,
		'phase-mapping': Object.keys(uniquePhaseMappingErrors).length,
		'controller-settings': Object.keys(controllerSettingsErrors).length,
		sequences: Object.keys(sequencesErrorObj).length,
		'tms-errors': tms_errors.count,
		'completed-configurations': 0,
	}

	const totalCount = Object.values(errorCounts).reduce((a, b) => a + b, 0)

	const signalErrors = {
		'phase-settings': { errors: phaseSettingsErrorObj, count: errorCounts['phase-settings'] },
		'coordination-settings': { errors: coordinationSettingsErrorObj, count: errorCounts['coordination-settings'] },
		'tod-schedule': { errors: timeOfDayErrors, count: errorCounts['tod-schedule'] },
		'phase-mapping': { errors: phaseMappingErrors, count: errorCounts['phase-mapping'] },
		'controller-settings': { errors: controllerSettingsErrors, count: errorCounts['controller-settings'] },
		sequences: { errors: sequencesErrorObj, count: errorCounts.sequences },
		'tms-errors': tms_errors,
		'completed-configurations': {
			errors: completedConfigurationsErrors,
			count: errorCounts['completed-configurations'],
		},
		'configuration-detials': completedConfigurationDetails,
		totalCount,
	}

	OptimusConfigurationModel.signal_errors[signalId] = signalErrors

	if (signalErrors.totalCount > 0) {
		return {
			errorCount: signalErrors.totalCount,
			signalStatus: SIGNAL_PROGRESS_STATE_ENUM['in-progress-errors'],
		}
	} else if (isNotStarted) {
		return {
			errorCount: 0,
			signalStatus: SIGNAL_PROGRESS_STATE_ENUM['not-started'],
		}
	} else if (isComplete) {
		return {
			errorCount: 0,
			signalStatus: SIGNAL_PROGRESS_STATE_ENUM['complete'],
		}
	}

	return {
		errorCount: 0,
		signalStatus: SIGNAL_PROGRESS_STATE_ENUM['in-progress-no-errors'],
	}
}

export const getAllSignalProgressStatus = () => {
	const signals = OptimusConfigurationModel.signals

	const filterMarkedForDeletion = (key: string, signalIdKey: string, settings: any[]) => {
		const filteredSettings = (settings || []).filter((setting) => {
			const isMarkedForDeletion = OptimusConfigurationModel.isMarkedForDeletion(
				key,
				setting[signalIdKey],
				'',
				setting.valid_from || null,
				setting.valid_to || null,
			)

			return !isMarkedForDeletion
		})

		return filteredSettings
	}
	const updatedSignals = signals.map((signal) => {
		const phaseSettings = OptimusConfigurationModel.phase_settings[signal.id] || []
		const modelCoordinationSettings = OptimusConfigurationModel.coordination_settings[signal.id] || []
		const coordinationSettings = filterMarkedForDeletion(
			'coordination_setting',
			'coordination_settings_id',
			modelCoordinationSettings,
		)
		const modelTimeOfDaySchedule = OptimusConfigurationModel.tod_schedules[signal.id] || []
		const timeOfDaySchedule = filterMarkedForDeletion('tod', 'time_of_day_id', modelTimeOfDaySchedule)

		const phaseMapping = OptimusConfigurationModel.phase_mappings[signal.id] || []
		const controllerSettings = OptimusConfigurationModel.controller_settings[signal.id] || []
		const sequences = OptimusConfigurationModel.sequences[signal.id] || []

		const tms_errors = OptimusConfigurationModel.signal_errors[signal.id]['tms-errors']

		if (!phaseSettings || !coordinationSettings || !timeOfDaySchedule || !phaseMapping || !sequences) {
			return signal
		}

		const progress = getSignalProgressStatus({
			phaseSettings: phaseSettings,
			coordinationSettings,
			timeOfDaySchedule,
			phaseMapping,
			controllerSettings,
			sequences,
			tms_errors,
			signal: signal,
		})
		return {
			...signal,
			...progress,
		}
	})

	OptimusConfigurationModel.signals = updatedSignals
	return updatedSignals
}

export const getUniqueTODValuesInOptimusConfiguration = (include_erroneous?: boolean) => {
	const unique_tods: TimeOfDayData[] = []

	Object.entries(
		(OptimusConfigurationModel.tod_schedules_all_versions as Record<string, TimeOfDayData[]>) || {},
	)?.forEach(([id, tod_schedules]) => {
		// HACK?: Since [network_id, tods] seems to be an entry for some reason...
		if (id === OptimusConfigurationModel.selected_network) {
			return
		}

		;(tod_schedules || [])?.forEach((t) => {
			// Only supports Optimus TODs, not legacy TODs
			if (t.time_of_day_id == null) {
				// console.log('legacy t', t)
				return
			}

			let has_errors = false
			if (!include_erroneous) {
				for (const signal_errors of Object.values(OptimusConfigurationModel.signal_errors || {})) {
					if (
						Object.keys(signal_errors?.['tod-schedule']?.errors ?? {}).some((key) => key.includes(t.time_of_day_id))
					) {
						has_errors = true
						break
					}
				}
			}

			if ((!include_erroneous && has_errors) || unique_tods.some((t2) => isSameTodDescription(t, t2))) {
				// console.log('erroneous or dupe t', t)
				return
			}

			unique_tods.push(t)
		})
	})

	return unique_tods
}

export const getFullyConfiguredTODDescriptions = ({ signal_id_filter }: { signal_id_filter?: string[] }) => {
	// A Fully configured TOD has completed Optimus Config documents at every signal's matching TOD
	const fully_configured_unique_tods: TimeOfDayData[] = []
	const errors: Record<string, string> = {}

	Object.entries(OptimusConfigurationModel.tod_schedules_all_versions as Record<string, TimeOfDayData[]>)?.forEach(
		([id, tod_schedules]) => {
			// HACK?: Since [network_id, tods] seems to be an entry for some reason...
			if (id === OptimusConfigurationModel.selected_network) {
				return
			}

			;(tod_schedules || [])?.forEach((t) => {
				if (
					t.time_of_day_id == null || // Only supports Optimus TODs, not legacy TODs
					fully_configured_unique_tods.some((t2) => isSameTodDescription(t, t2))
				) {
					// console.log('legacy or dupe t', t)
					return
				}

				const signal_id_to_matching_tod_schedules = Object.entries(
					(OptimusConfigurationModel.tod_schedules_all_versions as Record<string, TimeOfDayData[]>) ?? {},
				).reduce<Record<string, TimeOfDayData>>((_signal_id_to_matching_tod_schedules, [sig_id, tod_schedules2]) => {
					// HACK?: Since [network_id, tods] seems to be an entry for some reason...
					if (sig_id === OptimusConfigurationModel.selected_network) {
						return _signal_id_to_matching_tod_schedules
					}

					if (signal_id_filter && !signal_id_filter.includes(sig_id)) {
						return _signal_id_to_matching_tod_schedules
					}

					const matching_tod = tod_schedules2?.find((t2) => isSameTodDescription(t, t2))
					if (!matching_tod) {
						return _signal_id_to_matching_tod_schedules
					}

					return {
						..._signal_id_to_matching_tod_schedules,
						[sig_id]: matching_tod,
					}
				}, {})

				// console.log('sigs2tods', signal_id_to_matching_tod_schedules)

				// Rule: A fully configured TOD must have a matching TOD at all signals
				const num_total_signals = signal_id_filter
					? OptimusConfigurationModel.signals.filter((sig) => signal_id_filter.includes(sig.id)).length
					: OptimusConfigurationModel.signals.length
				if (Object.keys(signal_id_to_matching_tod_schedules).length !== num_total_signals) return

				// Rule: A fully configured TOD must have a full set of error-free documents
				// NOTE: Unlike filterFullyConfiguredDateRangeOptionsForTODDescription which is called later to filter allowed_date_ranges,
				//       here we do not perform version-related checks. Instead, we simply check for the existence of each document and that they are error-free.
				// TODO: Add version-related checks. Should make sure that signal_errors supports version-specific errors for the same document ID.
				const tod_is_fully_configured = Object.entries(signal_id_to_matching_tod_schedules).every(([sig_id, ts]) => {
					// console.log('ts', ts)
					const phase_setting = OptimusConfigurationModel.phase_settings?.[sig_id]?.find(
						(ps) => ps.phase_settings_id === ts.phase_settings_id,
					)
					const phase_mapping = OptimusConfigurationModel.phase_mappings?.[sig_id]?.find(
						(pm) => pm.phase_settings_id === phase_setting?.phase_settings_id,
					)
					const coordination_setting = OptimusConfigurationModel.coordination_settings?.[sig_id]?.find(
						(cs) => cs.coordination_settings_id === ts.coordination_settings_id,
					)

					if (!(phase_setting && phase_mapping && coordination_setting)) {
						const error_key = `${sig_id}-missing-setting`
						const missing_settings: string[] = []
						if (!phase_setting) {
							missing_settings.push('Phase Setting')
						}
						if (!phase_mapping) {
							missing_settings.push('Phase Mapping')
						}
						if (!coordination_setting) {
							missing_settings.push('Coordination Setting')
						}
						errors[error_key] = `${ts.name}: Missing ${missing_settings.join(', ')}`
						// console.log('missing docs', missing_settings)
						return false
					}

					const signal_errors = OptimusConfigurationModel.signal_errors[sig_id]

					const ps_errors = signal_errors['phase-settings']?.errors?.[phase_setting?.phase_settings_id] ?? {}
					const num_ps_errors = Object.keys(ps_errors).length

					const pm_errors = Object.fromEntries(
						Object.entries(signal_errors['phase-mapping']?.errors ?? {}).filter(([key, _]) =>
							key.includes(phase_setting?.phase_settings_id),
						),
					)
					const num_pm_errors = Object.keys(pm_errors).length

					const cs_errors = Object.fromEntries(
						Object.entries(signal_errors['coordination-settings']?.errors ?? {}).filter(([key, _]) =>
							key.includes(coordination_setting?.coordination_settings_id),
						),
					)
					const num_cs_errors = Object.keys(cs_errors).length

					const ts_errors = Object.fromEntries(
						Object.entries(signal_errors['tod-schedule']?.errors ?? {}).filter(([key, _]) =>
							key.includes(ts.time_of_day_id),
						),
					)
					const num_ts_errors = Object.keys(ts_errors).length

					if ([num_ps_errors, num_pm_errors, num_cs_errors, num_ts_errors].some((x) => x > 0)) {
						// console.log('errors (phase, mapping, coors, tod)',
						// 	ps_errors,
						// 	pm_errors,
						// 	cs_errors,
						// 	ts_errors,
						// )
						return false
					}

					return true
				})

				if (!tod_is_fully_configured) {
					return
				}

				fully_configured_unique_tods.push(t)
			})
		},
	)

	return {
		tods: fully_configured_unique_tods,
		errors,
	}
}

export interface DateRangeOption {
	key: string
	title: string
	date_start: string
	date_end: string
}

export const filterFullyConfiguredDateRangeOptionsForTODDescription = (
	date_range_options: DateRangeOption[],
	tod_description: string | undefined,
	signal_id_filter?: string[],
) => {
	if (!tod_description) {
		// console.log('1', tod_description)
		return []
	}

	// Rule: Every signal must have TODs for anything to be considered Fully Configured
	const num_total_signals = signal_id_filter
		? OptimusConfigurationModel.signals.filter((sig) => signal_id_filter.includes(sig.id)).length
		: OptimusConfigurationModel.signals.length
	if (
		Object.keys(OptimusConfigurationModel.tod_schedules_all_versions)
			// HACK?: Since [network_id, tods] seems to be an entry for some reason...
			.filter((id) => id !== OptimusConfigurationModel.selected_network)
			.filter((sig_id) => (signal_id_filter ? signal_id_filter.includes(sig_id) : true)).length !== num_total_signals
	) {
		// console.log('1', OptimusConfigurationModel.signals, OptimusConfigurationModel.tod_schedules_all_versions)
		return []
	}

	function parseValidRange(vr: string | undefined) {
		if (!vr) return [undefined, undefined]
		return vr
			.slice(1, -1) // Remove parentheses
			.replaceAll(/['"]/g, '') // Remove any quotes
			.split(',')
			.map((str) => str.slice(0, 10)) // Only retain YYYY-MM-DD OR infinity
	}

	// For a given TOD description, "fully configured date ranges" have completed Optimus Config documents across all signals for their respective matching TOD version within the date range
	// WARNING: Assumes current construction where Document Versions' valid_ranges are always in line with TOD Versions' valid_ranges
	const fully_configured_date_range_options = date_range_options.filter((dr) => {
		// console.log('DR', dr)

		const signals_x_tod_versions = (
			Object.entries(OptimusConfigurationModel.tod_schedules_all_versions ?? {}) as [string, TimeOfDayData[]][]
		).filter(([id, tod_versions]) => id !== OptimusConfigurationModel.selected_network) // HACK?: Since [network_id, tods] seems to be an entry for some reason...

		// console.log('???', signals_x_tod_versions, OptimusConfigurationModel.tod_schedules_all_versions)

		for (let sig_idx = 0; sig_idx < signals_x_tod_versions.length; sig_idx++) {
			const sig_id = signals_x_tod_versions[sig_idx][0]
			const tv = signals_x_tod_versions[sig_idx][1]?.find((tv) => {
				if (!tv.valid_range) {
					// console.log('NO VALID RANGE')
					return false
				}

				const [valid_from, valid_to] = parseValidRange(tv.valid_range)

				// console.log(
				// 	'tv',
				// 	tv,
				// 	tod_description,
				// 	generateTodDescription(tv.time_of_day_data),
				// 	generateTodDescription(tv.time_of_day_data) === tod_description,
				// 	isDateRangeCoversDateRange(
				// 		{date_start: valid_from, date_end: valid_to},
				// 		{date_start: dr.date_start, date_end: dr.date_end},
				// 	)
				// )

				return (
					generateTodDescription(tv.time_of_day_data) === tod_description &&
					isDateRangeCoversDateRange(
						{ date_start: valid_from, date_end: valid_to },
						{ date_start: dr.date_start, date_end: dr.date_end },
					)
				)
			})

			// Rule: Every signal must have a TOD version in the date range
			if (!tv) {
				// console.log('NO TV')
				return false
			}

			// console.log('TV!', tv)

			const phase_setting = OptimusConfigurationModel.phase_settings_all_versions?.[sig_id]?.find((ps) => {
				const [valid_from, valid_to] = parseValidRange(ps.valid_range)
				return (
					ps.phase_settings_id === tv.phase_settings_id &&
					isDateRangeCoversDateRange(
						{ date_start: valid_from, date_end: valid_to },
						{ date_start: dr.date_start, date_end: dr.date_end },
					)
				)
			})
			const phase_mapping = OptimusConfigurationModel.phase_mappings_all_versions?.[sig_id]?.find((pm) => {
				// phase mappings don't need to check valid ranges, add when we support archiving phase mappings
				return pm.phase_settings_id === phase_setting?.phase_settings_id
			})
			const coordination_setting = OptimusConfigurationModel.coordination_settings_all_versions?.[sig_id]?.find(
				(cs) => {
					const [valid_from, valid_to] = parseValidRange(cs.valid_range)
					return (
						cs.coordination_settings_id === tv.coordination_settings_id &&
						isDateRangeCoversDateRange(
							{ date_start: valid_from, date_end: valid_to },
							{ date_start: dr.date_start, date_end: dr.date_end },
						)
					)
				},
			)

			// console.log('any signal missing doc?', sig_id, tv, phase_setting, phase_mapping, coordination_setting)

			// Rule: The TOD version must have a full set of corresponding document versions for the date range
			if (!(phase_setting && phase_mapping && coordination_setting)) {
				return false
			}

			// Rule: Every document version must have no errors
			const signal_errors = OptimusConfigurationModel.signal_errors[sig_id]
			const ps_errors = signal_errors['phase-settings']?.errors?.[phase_setting?.phase_settings_id] ?? {}
			const num_ps_errors = Object.keys(ps_errors).length

			const pm_errors = Object.fromEntries(
				Object.entries(signal_errors['phase-mapping']?.errors ?? {}).filter(([key, _]) =>
					key.includes(phase_setting?.phase_settings_id),
				),
			)
			const num_pm_errors = Object.keys(pm_errors).length

			const cs_errors = Object.fromEntries(
				Object.entries(signal_errors['coordination-settings']?.errors ?? {}).filter(([key, _]) =>
					key.includes(coordination_setting?.coordination_settings_id),
				),
			)
			const num_cs_errors = Object.keys(cs_errors).length

			const ts_errors = Object.fromEntries(
				Object.entries(signal_errors['tod-schedule']?.errors ?? {}).filter(([key, _]) =>
					key.includes(tv.time_of_day_id),
				),
			)
			const num_ts_errors = Object.keys(ts_errors).length

			if ([num_ps_errors, num_pm_errors, num_cs_errors, num_ts_errors].some((x) => x > 0)) {
				//console.log('errors (phase, mapping, coors, tod)',
				//	ps_errors,
				//	pm_errors,
				//	cs_errors,
				//	ts_errors,
				//)
				return false
			}
		}

		// console.log('PASS')
		return true
	})

	return fully_configured_date_range_options
}

export const getTODDescriptionFromTODSlice = (tod_slice: SignalConfigurationTODSlice) => {
	return generateTodDescription({
		days: tod_slice.days,
		months: tod_slice.months ?? undefined,
		days_of_month: tod_slice.days_of_month ?? undefined,
		local_start_time: tod_slice.local_start_time,
		local_end_time: tod_slice.local_end_time,
	})
}

export const getFirstMinimalOptimusJobConfigForSignal = (signal) => {
	// !!! WARNING !!!
	// This DOES NOT guarantee that the configs are actually related to each other!

	const signal_errors = OptimusConfigurationModel.signal_errors[signal.id]

	// Ideally, most docs will be linked to a tod_schedule to preserve their coordination.
	// So we prioritize docs related to tod_schedule first and fallback to a random doc.
	const tod_schedule = OptimusConfigurationModel.tod_schedules?.[signal.id]?.find(
		(ts) =>
			Object.entries(signal_errors['tod-schedule']?.errors ?? {}).filter(([key, _]) => key.includes(ts.time_of_day_id))
				?.length === 0,
	)

	/*
		Minimum Required Config
	*/

	// PhaseSettings - Must be error-free and have at least 1 Enabled Phase and 1 Coordinated Phase
	const phase_setting =
		OptimusConfigurationModel.phase_settings?.[signal.id]?.find(
			(ps) =>
				tod_schedule?.phase_settings_id === ps.phase_settings_id &&
				Object.keys(signal_errors['phase-settings']?.errors?.[ps.phase_settings_id] || {}).length === 0 &&
				Object.values(ps.phase_settings_data).some((phase_data) => phase_data.enabled) &&
				Object.values(ps.phase_settings_data).some((phase_data) => phase_data.coordinated),
		) ??
		OptimusConfigurationModel.phase_settings?.[signal.id]?.find(
			(ps) =>
				Object.keys(signal_errors['phase-settings']?.errors?.[ps.phase_settings_id] || {}).length === 0 &&
				Object.values(ps.phase_settings_data).some((phase_data) => phase_data.enabled) &&
				Object.values(ps.phase_settings_data).some((phase_data) => phase_data.coordinated),
		)

	// Controller Settings - Must be error-free and have offset_reference_point
	const controller_setting = OptimusConfigurationModel.controller_settings?.[signal.id]?.find(
		(cs) =>
			Object.keys(signal_errors['controller-settings']?.errors?.[cs.controller_settings_id] || {}).length === 0 &&
			cs.controller_settings_data?.offsetReferencePoint != null,
	)

	// Phase Mapping - Must be error-free
	// Prefer doc related to tod's phase setting, then a random phase setting, then fallback to a random doc
	const phase_mapping =
		OptimusConfigurationModel.phase_mappings?.[signal.id]?.find(
			(pm) =>
				phase_setting?.phase_settings_id === pm.phase_settings_id &&
				Object.keys(signal_errors['phase-mapping']?.errors ?? {})?.filter((key) => key.includes(pm.phase_settings_id))
					.length === 0,
		) ??
		OptimusConfigurationModel.phase_mappings?.[signal.id]?.find(
			(pm) =>
				Object.keys(signal_errors['phase-mapping']?.errors ?? {})?.filter((key) => key.includes(pm.phase_settings_id))
					.length === 0,
		)

	// Sequence - Must be error-free
	const sequence =
		OptimusConfigurationModel.getSequences(signal.id)?.find(
			(s) =>
				tod_schedule?.sequence_id === s.sequence_id &&
				Object.keys(signal_errors.sequences?.errors || {})?.filter((key) => key.includes(s.sequence_external_id))
					.length === 0,
		) ??
		OptimusConfigurationModel.getSequences(signal.id)?.find(
			(s) =>
				Object.keys(signal_errors.sequences?.errors || {})?.filter((key) => key.includes(s.sequence_external_id))
					.length === 0,
		)

	const has_minimum_set = phase_setting && controller_setting && phase_mapping && sequence

	if (!has_minimum_set) {
		return undefined
	}

	// Optional Docs for Convenience
	const coordination_setting =
		OptimusConfigurationModel.coordination_settings?.[signal.id]?.find(
			(cs) =>
				tod_schedule?.coordination_settings_id === cs.coordination_settings_id &&
				Object.keys(signal_errors['coordination-settings']?.errors || {})?.filter((key) =>
					key.includes(cs.coordination_settings_id),
				).length === 0,
		) ??
		OptimusConfigurationModel.coordination_settings?.[signal.id]?.find(
			(cs) =>
				Object.keys(signal_errors['coordination-settings']?.errors || {})?.filter((key) =>
					key.includes(cs.coordination_settings_id),
				).length === 0,
		)

	return {
		phase_setting,
		controller_setting,
		phase_mapping,
		sequence,

		optional_docs: {
			tod_schedule,
			coordination_setting,
		},
	}
}

export enum OPTIMUS_JOB_SIGNAL_STATUS {
	BELOW_MINIMUM = 'BELOW_MINIMUM',
	MET_MINIMUM = 'MET_MINIMUM',
	COMPLETE = 'COMPLETE',
}

export const getOptimusJobSignalStatus = (signal) => {
	// BEGIN TOD_ERRORS_IGNORED_FOR_OPTIMUS_COMPLETE
	// TOD-schedule errors do not block a signal from counting as COMPLETE for Optimus.
	// A signal is treated as complete if it is already complete, or if its only outstanding
	// errors are on the tod-schedule tab (i.e. every other tab is error-free).
	// TO REVERT: delete this whole block and restore the single original line:
	//   const signal_status_is_complete = signal?.signalStatus === SIGNAL_PROGRESS_STATE_ENUM.complete
	const signal_errors = OptimusConfigurationModel.signal_errors[signal.id]
	const non_tod_error_count = (signal_errors?.totalCount ?? 0) - (signal_errors?.['tod-schedule']?.count ?? 0)
	const signal_status_is_complete =
		signal?.signalStatus === SIGNAL_PROGRESS_STATE_ENUM.complete ||
		(signal?.signalStatus === SIGNAL_PROGRESS_STATE_ENUM['in-progress-errors'] && non_tod_error_count === 0)
	// END TOD_ERRORS_IGNORED_FOR_OPTIMUS_COMPLETE

	// BEGIN TEMP HACK - Since sequences don't seem to be factored in signalStatus yet?
	const error_free_sequence = OptimusConfigurationModel.getSequences(signal.id)?.find(
		(s) =>
			Object.keys(OptimusConfigurationModel.signal_errors[signal.id].sequences?.errors || {})?.filter((key) =>
				key.includes(s.sequence_external_id),
			).length === 0,
	)
	// END TEMP HACK - Since sequences don't seem to be factored in signalStatus yet?

	if (signal_status_is_complete && error_free_sequence) {
		return OPTIMUS_JOB_SIGNAL_STATUS.COMPLETE
	}

	const minimal_job_config = getFirstMinimalOptimusJobConfigForSignal(signal)
	if (minimal_job_config) {
		return OPTIMUS_JOB_SIGNAL_STATUS.MET_MINIMUM
	}

	return OPTIMUS_JOB_SIGNAL_STATUS.BELOW_MINIMUM
}
